import type { ReactNode } from "react";
import { useQueries } from "@tanstack/react-query";
import {
  roleGrantPreflightApi,
  type RoleGrantHeldEffect,
  type RoleGrantPreflightDto,
} from "@/api/users";
import { roleGrantKeys } from "@/hooks/users/roleGrantKeys";
import { formatCurrency } from "@/design-system/lib/format";

/** The Buyer Manager and the Buyer Consultant — the two BOOK positions, mirroring
 *  `PhoenixCapital.Core.Constants.CommissionBookRoles`. Compared case-insensitively,
 *  as every role-code set on the server is: a role code is a plain `nvarchar`
 *  with no value converter, so casing must never decide who is paid. */
const F1 = "F1";
const F2 = "F2";

/** The Broker Liaison. Named here only for the pairing below — C1 is a deal-pill
 *  position and a grant of it routes nothing on its own. */
const C1 = "C1";

/** Case-insensitive membership, for the same reason the server's sets are
 *  `OrdinalIgnoreCase`. */
function has(codes: readonly string[], code: string): boolean {
  return codes.some((c) => c.toLowerCase() === code.toLowerCase());
}

/**
 * A neutral statement of what the role codes on this form will do to commission
 * money, rendered above the Save button on both role-grant surfaces.
 *
 * 🔴 A DISCLOSURE, NEVER A GATE. It blocks nothing, disables nothing, and takes
 * no position on whether the grant is right — the person making it is
 * authorised to make it. What was missing is that the consequence was invisible
 * at the moment somebody chose to cause it: granting a role code writes a
 * `UserRoleCodes` row and calls nothing.
 *
 * 🔴 IT TAKES THE FULL SELECTED SET, NOT JUST THE ADDITIONS, and that is a
 * requirement rather than a convenience. The per-code sentences still describe
 * ADDITIONS only — re-saving a code somebody already holds writes the same row
 * and routes nothing, and attaching a money consequence to an act that has none
 * is how a notice gets learned past. But the C1 + F1 pairing is a fact about the
 * RESULTING SET: a liaison being granted F1 and an F1 holder being granted C1
 * arrive from opposite directions and mean the same thing, and neither is
 * visible from the delta alone.
 *
 * 🔴 IT SPEAKS IN THREE DIRECTIONS, and the third is the correction. A
 * FIRM-WIDE position resolves only when EXACTLY ONE person holds the code, so
 * at zero existing holders a grant RELEASES held roll-ups and at one it FREEZES
 * the position. A BOOK position (F1, F2) resolves per loan from that loan's own
 * frozen attribution and does neither — several people hold a book code at once
 * by design. Before the Book tier existed, both fell through the firm-wide rule
 * and this surface told an administrator granting F1 to a second Buyer Manager
 * that "adding another stops it resolving": a confident sentence about a count
 * that decides nothing, offering the one remedy that cannot work. Silence would
 * have been the next wrong answer, which is why a book code still speaks here.
 *
 * The wording is FACT, not caution — no "warning", no "are you sure". It
 * describes what the system will do, which is what the person needs in order to
 * decide, and treating a legitimate act as suspect is how a notice gets learned
 * past.
 */
export function RoleGrantCommissionNotice({
  selectedRoleCodes,
  existingRoleCodes = [],
}: {
  /** Every code the user will hold after Save — the checkbox grid's own state. */
  selectedRoleCodes: string[];
  /** Every code they hold TODAY. Empty on the invite path, where the user does
   *  not exist yet and every code is an addition by construction. */
  existingRoleCodes?: string[];
}) {
  const added = selectedRoleCodes.filter((c) => !has(existingRoleCodes, c));

  // One query per newly-added code. Each is two small indexed reads server-side
  // and the list is a handful of checkboxes, so a fan-out is cheaper than the
  // batch endpoint it would take to avoid it.
  const results = useQueries({
    queries: added.map((code) => ({
      queryKey: roleGrantKeys.preflight(code),
      queryFn: () => roleGrantPreflightApi.describe(code),
      // Held roll-ups move only on an hourly sweep, so a short cache is honest
      // and keeps re-ticking a checkbox from re-querying.
      staleTime: 60_000,
    })),
  });

  const notable = results
    .map((r) => r.data)
    .filter((d): d is NotablePreflight => !!d && d.effect !== "None");

  // 🔴 THE PAIRING IS READ OFF THE RESULTING SET AND GATED ON THE DELTA. Both
  // halves matter. Off the resulting set, because a liaison granted F1 and an F1
  // holder granted C1 create the same state and only one of them has F1 in the
  // additions. Gated on the delta, because an administrator opening this form
  // for somebody who already holds both and changing something unrelated has not
  // caused the supersession, and saying so would attach it to the wrong act.
  const supersedesC1 =
    has(selectedRoleCodes, C1) &&
    has(selectedRoleCodes, F1) &&
    (has(added, C1) || has(added, F1));

  // Silence is the correct output for the ordinary case: most grants touch no
  // commission money at all, and a notice that renders on every save is one
  // nobody reads by the third time.
  if (notable.length === 0 && !supersedesC1) return null;

  return (
    <div
      className="mt-space-4 p-space-3 border border-status-warning rounded-md bg-status-warning/5 text-small"
      role="note"
    >
      <span className="font-display font-semibold uppercase text-label tracking-label text-status-warning">
        Commission effect
      </span>
      <ul className="flex flex-col gap-space-2 m-0 mt-space-2 p-0 list-none text-onyx">
        {notable.map((d) => (
          <li key={d.roleCode}>{EFFECT_COPY[d.effect](d)}</li>
        ))}
        {supersedesC1 && (
          <li key="c1-superseded-by-f1">
            This user will hold both <Code>{C1}</Code> and <Code>{F1}</Code>.{" "}
            <Code>{F1}</Code> includes the broker access <Code>{C1}</Code> gives, so on a
            loan whose F1 book owner is also the deal's broker liaison — the same person —
            the <Code>{C1}</Code> share is not paid: Phoenix keeps it, from the day their
            F1 share on that loan begins. Other liaisons' <Code>{C1}</Code> shares are
            untouched, and so are theirs on loans outside their F1 book.
          </li>
        )}
      </ul>
    </div>
  );
}

/** A role code, rendered as one. Written once so eight call sites cannot drift
 *  into eight slightly different spans. */
function Code({ children }: { children: ReactNode }) {
  return <span className="font-mono">{children}</span>;
}

/** A pre-flight the notice has something to say about. `None` is filtered out
 *  before the dispatch below, which is why it is excluded from the key type. */
type NotablePreflight = RoleGrantPreflightDto & {
  effect: Exclude<RoleGrantHeldEffect, "None">;
};

/**
 * What each effect reads as.
 *
 * 🔴 A `Record<Exclude<RoleGrantHeldEffect, "None">, …>` AND NOT A TERNARY, and
 * the history is the argument. The dispatch here WAS a ternary with two arms,
 * and when the Book tier arrived every book code fell into the `else` — which
 * said "this position pays a single firm-wide holder, so adding another stops
 * it resolving". False in every clause, on a role two people are meant to hold.
 * A Record makes an effect added to the server vocabulary without copy a
 * TYPECHECK ERROR rather than a sentence invented for it by whichever arm
 * happens to be last.
 *
 * The server's side is an enum no compiler checks against this union, so the
 * other half of the guarantee is that both are edited together — the enum's own
 * member doc names this file.
 */
const EFFECT_COPY: Record<
  Exclude<RoleGrantHeldEffect, "None">,
  (d: NotablePreflight) => ReactNode
> = {
  RoutesHeldRollups: (d) => (
    <>
      Granting <Code>{d.roleCode}</Code> will route {d.heldRollupCount} held monthly
      roll-up
      {d.heldRollupCount === 1 ? "" : "s"} totalling about{" "}
      {formatCurrency(d.heldRollupTotal, {
        // `formatCurrency` drops cents by default, which is right for a headline
        // tile and wrong here: this figure is the sum of rows a reader can open
        // in All Claims, where amounts render exact. Rounding would add a SECOND
        // discrepancy on top of the one the "about" already discloses, and an
        // unexplainable one.
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}{" "}
      to this user on the next hourly sweep. Nobody holds <Code>{d.roleCode}</Code> today,
      so this grant is what makes the position resolve.{" "}
      {/* "about" is load-bearing: the sweep REDRAFTS rather than reassigns, and
          the redraft re-prices against any per-user rate override the grantee
          carries, so the amount that lands can differ from the held row it
          replaces. */}
      Covers the last {d.lookbackMonths} closed months — older roll-ups are not
      revisited.
    </>
  ),

  FreezesFutureRollups: (d) => (
    <>
      <Code>{d.roleCode}</Code> is already held by{" "}
      {d.currentHolderCount === 1 ? "one person" : `${d.currentHolderCount} people`}. This
      position pays a single firm-wide holder, so adding another stops it resolving:{" "}
      {d.heldRollupCount === 1
        ? "the 1 held roll-up stays held, and "
        : d.heldRollupCount > 1
          ? `the ${d.heldRollupCount} held roll-ups stay held, and `
          : ""}
      future months will hold too, with no further notice. Take the code off the others to
      route it again.
    </>
  ),

  // 🔴 NO HOLDER COUNT IN THIS ARM, AND THAT IS NOT AN OMISSION. The server runs
  // the count query only for the firm-wide tier, so `currentHolderCount` is ZERO
  // here whatever the truth is — rendering it would state, of a role several
  // people hold by design, that nobody holds it.
  RoutedByBook: (d) => (
    <>
      {BOOK_LEAD[d.roleCode.toUpperCase()] ??
        "This position is paid on a book rather than on a firm-wide seat."}{" "}
      Several people hold <Code>{d.roleCode}</Code> at once by design, so this grant
      neither starts nor stops anybody else being paid. It routes no held roll-up either:
      a held book share is held by a fact about the LOAN, and only an ADMIN correction to
      that loan's commission attribution releases it.
    </>
  ),
};

/**
 * What each book position's book IS, keyed by role code.
 *
 * ⚠️ The fallback is a lookup MISS, not a false-shaped `else`, and the
 * difference is why this is a plain record with a `??` while the dispatch above
 * refuses one. "Paid on a book rather than on a firm-wide seat" is true of every
 * member of `CommissionBookRoles` by definition, so a third book code arriving
 * without copy here reads as less specific rather than as wrong — where the
 * old `else` arm asserted a firm-wide count rule about a position that has none.
 */
const BOOK_LEAD: Record<string, string> = {
  [F1]:
    "A Buyer Manager is paid on their own book — the buyers worked by the consultants on the team they manage, and the brokers they liaise.",
  [F2]:
    "A Buyer Consultant is paid on their own book — the buyers assigned to and worked by them.",
};
